import { Info } from 'lucide-react';
import { useMemo } from 'react';

/**
 * Preview informativo da URL final que o lead receberá após o redirect.
 *
 * Backend (`auto_utm_builder.py`) injeta automaticamente no momento do click:
 *   - utm_source  = facecap_{page_id} (flow) | facebroad_{page_id} (broadcast)
 *   - utm_content = flow-{funnel_id}_node-{node_id} | broadcast-{broadcast_id}
 *   - utm_term    = flow | broadcast
 *
 * Esse componente NÃO altera o que está sendo salvo no banco — a URL
 * permanece pura. É só visualização pro user entender o que vai sair.
 *
 * `page_id` é mostrado como `{page_id}` literal porque depende do lead
 * que clica: cada lead tem uma page diferente, então a URL final muda
 * por lead.
 */
interface UtmPreviewProps {
    url: string;
    mode: 'flow' | 'broadcast';
    /** Para mode='flow': IDs do funil e do node de origem */
    funnelId?: number;
    nodeId?: number;
    /** Para mode='broadcast': ID do broadcast */
    broadcastId?: number;
    /** Compacto = sem header explicativo (útil quando há vários previews juntos) */
    compact?: boolean;
}

function buildPreviewUrl({
    url,
    mode,
    funnelId,
    nodeId,
    broadcastId,
}: UtmPreviewProps): string | null {
    if (!url || !url.trim()) return null;

    // Garante protocolo pra urlparse funcionar
    let working = url.trim();
    if (!/^https?:\/\//i.test(working)) {
        working = 'https://' + working;
    }

    // Pra preview, usa placeholder visível pro page_id (depende do lead)
    // e {broadcast_id}/{funnel_id}/{node_id} quando o id real ainda não existir
    // (ex: broadcast em criação).
    const auto: Record<string, string> = {};
    if (mode === 'flow') {
        auto.utm_source = 'facecap_{page_id}';
        const f = funnelId ?? '{funnel_id}';
        const n = nodeId ?? '{node_id}';
        auto.utm_content = `flow-${f}_node-${n}`;
        auto.utm_term = `flow_${f}`;
    } else {
        auto.utm_source = 'facebroad_{page_id}';
        const b = broadcastId ?? '{broadcast_id}';
        auto.utm_content = `broadcast-${b}`;
        auto.utm_term = `broadcast_${b}`;
    }

    try {
        const parsed = new URL(working);
        for (const [k, v] of Object.entries(auto)) {
            parsed.searchParams.set(k, v);
        }
        // URL encoda os {} — devolve readable pro preview
        return decodeURI(parsed.toString());
    } catch {
        // URL inválida — retorna template legível mesmo assim
        const sep = working.includes('?') ? '&' : '?';
        const qs = Object.entries(auto)
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
        return `${working}${sep}${qs}`;
    }
}

export default function UtmPreview(props: UtmPreviewProps) {
    const { url, compact } = props;
    const previewUrl = useMemo(() => buildPreviewUrl(props), [
        props.url, props.mode, props.funnelId, props.nodeId, props.broadcastId,
    ]);

    if (!url || !url.trim() || !previewUrl) return null;

    return (
        <div className="mt-1.5 text-[10px] text-gray-500 leading-relaxed">
            {!compact && (
                <div className="flex items-center gap-1 mb-0.5">
                    <Info className="w-3 h-3 text-cyan-500" />
                    <span className="font-medium text-cyan-700">URL final no clique:</span>
                </div>
            )}
            <code className="block bg-cyan-50/60 border border-cyan-100 rounded px-2 py-1 font-mono text-[10px] text-cyan-900 break-all">
                {previewUrl}
            </code>
            <p className="text-[10px] text-gray-400 mt-0.5">
                <span className="font-mono">{`{page_id}`}</span> é trocado pela página do lead no clique. UTMs auto sobrescrevem se já estiverem na URL.
            </p>
        </div>
    );
}
